// Keep preview URLs inert until a fine, hover-capable pointer is available.
const previewMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
const cardImages = new Set();
const previewObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      for (const { target, isIntersecting } of entries) {
        if (isIntersecting) target.loadPreview();
      }
    }, { rootMargin: '600px 0px' })
  : null;

class CardImage extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-has-secondary')) return;
    cardImages.add(this);
    this.updatePreviewCapability();
  }

  disconnectedCallback() {
    previewObserver?.unobserve(this);
    cardImages.delete(this);
  }

  updatePreviewCapability() {
    if (!previewMedia.matches) {
      previewObserver?.unobserve(this);
      this.removeAttribute('data-preview-ready');
      this.querySelector('.card-image__secondary')?.remove();
      return;
    }
    if (previewObserver) previewObserver.observe(this);
    else this.loadPreview();
  }

  async loadPreview() {
    if (!previewMedia.matches || !this.isConnected) return;
    previewObserver?.unobserve(this);
    if (this.querySelector('.card-image__secondary')) return;

    const template = this.querySelector('template[data-card-image-preview]');
    const image = template?.content.querySelector('img')?.cloneNode(true);
    if (!(image instanceof HTMLImageElement)) return;

    // This is intentional desktop-only proximity prefetch. Native lazy loading
    // on an invisible overlay would otherwise defer it until the actual hover.
    image.loading = 'eager';
    this.append(image);
    try {
      await image.decode();
      if (previewMedia.matches && image.parentElement === this) {
        this.setAttribute('data-preview-ready', '');
      }
    } catch {
      // An unavailable preview must never replace a working primary image.
      image.remove();
    }
  }
}

previewMedia.addEventListener('change', () => {
  for (const cardImage of cardImages) cardImage.updatePreviewCapability();
});

if (!customElements.get('card-image')) customElements.define('card-image', CardImage);
